import { useState } from 'react'

import { useModalDialog } from '@procurify/ui'
import { noop } from 'lodash'
import { useParams } from 'react-router'

import { toast } from '@webapp/components/Notification'
import {
  useAppRoutes,
  useLocationHelper,
  useProcurifyIntl,
} from '@webapp/hooks'
import { type LegalEntity } from '@webapp/platform/legal-entities/entities/LegalEntity'
import {
  type LegalEntityListPageParams,
  LegalEntityListPageTabTypes,
} from '@webapp/platform/legal-entities/pages/types'
import messages from './messages'

interface FeatureSetTab {
  label: string
  to: string
}

export const useLegalEntityListFeatureSet = () => {
  const intl = useProcurifyIntl()
  const { tab } = useParams<LegalEntityListPageParams>()
  const { url } = useAppRoutes()
  const { getQueryString } = useLocationHelper()

  const [openCreateModal, closeCreateModal, createModalStates] =
    useModalDialog()
  const [openEditModal, closeEditModal, editModalStates] = useModalDialog()

  const [editingEntity, setEditingEntity] =
    useState<Nullable<LegalEntity>>(null)

  const isActive = tab === LegalEntityListPageTabTypes.ACTIVE

  const tabs: FeatureSetTab[] = [
    {
      label: intl.formatMessage(messages.activeTabLabel),
      to: `${url('LegalEntityListPage', {
        tab: LegalEntityListPageTabTypes.ACTIVE,
      })}${getQueryString({ page: 1 })}`,
    },
    {
      label: intl.formatMessage(messages.inactiveTabLabel),
      to: `${url('LegalEntityListPage', {
        tab: LegalEntityListPageTabTypes.INACTIVE,
      })}${getQueryString({ page: 1 })}`,
    },
  ]

  const handleCreateSuccess = () => {
    toast.success({
      title: intl.formatMessage(messages.createSuccessToastTitle),
    })
    closeCreateModal()
  }

  // Edit is launched from the list row ⋯ menu for now; it moves to the entity
  // details page in PLAT-4859 (reusing the same edit modal).
  const handleEditEntity = (entity: LegalEntity) => {
    setEditingEntity(entity)
    openEditModal()
  }

  const handleEditSuccess = () => {
    toast.success({
      title: intl.formatMessage(messages.editSuccessToastTitle),
    })
    closeEditModal()
  }

  return {
    state: {
      isActive,
      tabs,
      createModalStates,
      editModalStates,
      editingEntity,
      editModalTitle: intl.formatMessage(messages.editModalTitle),
    },
    methods: {
      onAdd: openCreateModal,
      closeCreateModal,
      handleCreateSuccess,
      onEdit: handleEditEntity,
      closeEditModal,
      handleEditSuccess,
      // TODO(PLAT-4859): navigate to entity details page.
      onSelect: noop,
      // TODO(PLAT-4860): deactivate/reactivate flow.
      onDeactivate: noop,
      onActivate: noop,
    },
  }
}
